/**
 * Create/Edit Identity Step
 *
 * Second step in billing setup wizard - creates or edits a billing identity.
 * User enters NPI, Tax ID, legal name, and billing address.
 * Supports editing an existing entity when coming back from failed verification.
 */

import { useEffect, useState } from 'react';
import { useBillingEntity, useBillingEntityOperations } from '@features/billing-entities';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { extractApiErrorMessage } from '../../utils/errorUtils';

interface CreateEntityStepProps {
  onNext: () => void;
  onBack: () => void;
  onEntityCreated: (entityId: string) => void;
  /** Optional existing entity ID for editing mode */
  existingEntityId?: string | null;
}

interface FormData {
  billing_npi: string;
  tax_id: string;
  tax_id_type: string;
  legal_name: string;
  doing_business_as: string;
  billing_address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  };
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

const initialFormData: FormData = {
  billing_npi: '',
  tax_id: '',
  tax_id_type: 'EIN',
  legal_name: '',
  doing_business_as: '',
  billing_address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  },
  contact_name: '',
  contact_email: '',
  contact_phone: '',
};

export function CreateEntityStep({ onNext, onBack, onEntityCreated, existingEntityId }: CreateEntityStepProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string> & { contact_email?: string; contact_phone?: string }>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Fetch existing entity if we have an ID
  const { entity: existingEntity, isLoading: entityLoading } = useBillingEntity(existingEntityId || null);

  const { create, createLoading, createError, createSuccess, update, updateLoading } = useBillingEntityOperations();
  const [createdEntityId, setCreatedEntityId] = useState<string | null>(existingEntityId || null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Pre-fill form with existing entity data
  useEffect(() => {
    if (existingEntity && !formInitialized) {
      setFormData({
        billing_npi: existingEntity.billing_npi || '',
        tax_id: '', // Can't pre-fill - it's masked
        tax_id_type: existingEntity.tax_id_type || 'EIN',
        legal_name: existingEntity.legal_name || '',
        doing_business_as: existingEntity.doing_business_as || '',
        billing_address: {
          line1: existingEntity.billing_address?.line1 || '',
          line2: existingEntity.billing_address?.line2 || '',
          city: existingEntity.billing_address?.city || '',
          state: existingEntity.billing_address?.state || '',
          zip: existingEntity.billing_address?.zip || '',
        },
        contact_name: existingEntity.contact_name || '',
        contact_email: existingEntity.contact_email || '',
        contact_phone: existingEntity.contact_phone || '',
      });
      setIsEditMode(true);
      setFormInitialized(true);
    }
  }, [existingEntity, formInitialized]);

  const handleChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddressChange =
    (field: keyof FormData['billing_address']) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData(prev => ({
        ...prev,
        billing_address: {
          ...prev.billing_address,
          [field]: event.target.value,
        },
      }));
    };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.billing_npi.trim() || !/^\d{10}$/.test(formData.billing_npi)) {
      newErrors.billing_npi = 'NPI must be exactly 10 digits';
    }

    // Tax ID validation - only required for new entities or if user is changing it
    if (!isEditMode) {
      if (!formData.tax_id.trim()) {
        newErrors.tax_id = 'Tax ID is required';
      } else if (formData.tax_id_type === 'EIN' && !/^\d{2}-?\d{7}$/.test(formData.tax_id)) {
        newErrors.tax_id = 'EIN must be in format XX-XXXXXXX or XXXXXXXXX';
      } else if (formData.tax_id_type === 'SSN' && !/^\d{3}-?\d{2}-?\d{4}$/.test(formData.tax_id)) {
        newErrors.tax_id = 'SSN must be in format XXX-XX-XXXX or XXXXXXXXX';
      }
    } else if (formData.tax_id.trim()) {
      // If editing and user provided a tax_id, validate it
      if (formData.tax_id_type === 'EIN' && !/^\d{2}-?\d{7}$/.test(formData.tax_id)) {
        newErrors.tax_id = 'EIN must be in format XX-XXXXXXX or XXXXXXXXX';
      } else if (formData.tax_id_type === 'SSN' && !/^\d{3}-?\d{2}-?\d{4}$/.test(formData.tax_id)) {
        newErrors.tax_id = 'SSN must be in format XXX-XX-XXXX or XXXXXXXXX';
      }
    }

    if (!formData.legal_name.trim()) {
      newErrors.legal_name = 'Legal name is required';
    }

    if (!formData.billing_address.line1.trim()) {
      newErrors.billing_address = 'Address line 1 is required' as string;
    }

    if (!formData.billing_address.city.trim()) {
      newErrors.billing_address = 'City is required' as string;
    }

    if (!formData.billing_address.state.trim() || formData.billing_address.state.length !== 2) {
      newErrors.billing_address = 'Valid 2-letter state code is required' as string;
    }

    if (!formData.billing_address.zip.trim() || !/^\d{5}(-\d{4})?$/.test(formData.billing_address.zip)) {
      newErrors.billing_address = 'Valid ZIP code is required (XXXXX or XXXXX-XXXX)' as string;
    }

    // Contact info is required
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }

    if (!formData.contact_phone.trim()) {
      newErrors.contact_phone = 'Contact phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitError(null);

    try {
      if (isEditMode && existingEntityId) {
        // Update existing entity
        const updatePayload: Record<string, unknown> = {
          billing_npi: formData.billing_npi.replace(/\D/g, ''),
          legal_name: formData.legal_name.trim(),
          doing_business_as: formData.doing_business_as.trim() || null,
          billing_address: {
            line1: formData.billing_address.line1.trim(),
            line2: formData.billing_address.line2.trim() || null,
            city: formData.billing_address.city.trim(),
            state: formData.billing_address.state.trim().toUpperCase(),
            zip: formData.billing_address.zip.trim(),
          },
          contact_name: formData.contact_name.trim() || null,
          contact_email: formData.contact_email.trim(),
          contact_phone: formData.contact_phone.trim(),
        };

        // Only include tax_id if user explicitly provided one (editing)
        if (formData.tax_id.trim()) {
          updatePayload.tax_id = formData.tax_id.replace(/\D/g, '');
          updatePayload.tax_id_type = formData.tax_id_type;
        }

        await update({
          billingEntityId: existingEntityId,
          updateBillingEntityRequest: updatePayload,
        }).unwrap();

        setCreatedEntityId(existingEntityId);
        onEntityCreated(existingEntityId);
        enqueueSnackbar('Billing identity updated! Try verifying again.', { variant: 'success' });
      } else {
        // Create new entity
        const result = await create({
          createBillingEntityRequest: {
            billing_npi: formData.billing_npi.replace(/\D/g, ''),
            tax_id: formData.tax_id.replace(/\D/g, ''),
            tax_id_type: formData.tax_id_type,
            legal_name: formData.legal_name.trim(),
            doing_business_as: formData.doing_business_as.trim() || null,
            billing_address: {
              line1: formData.billing_address.line1.trim(),
              line2: formData.billing_address.line2.trim() || null,
              city: formData.billing_address.city.trim(),
              state: formData.billing_address.state.trim().toUpperCase(),
              zip: formData.billing_address.zip.trim(),
            },
            contact_name: formData.contact_name.trim() || null,
            contact_email: formData.contact_email.trim(),
            contact_phone: formData.contact_phone.trim(),
            is_default: true,
          },
        }).unwrap();

        if (result && 'billing_entity_id' in result) {
          setCreatedEntityId(result.billing_entity_id);
          onEntityCreated(result.billing_entity_id);
          enqueueSnackbar('Billing identity added!', { variant: 'success' });
        }
      }
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        `Failed to ${isEditMode ? 'update' : 'add'} billing identity. Please check your information and try again.`,
      );
      setSubmitError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} billing identity:`, err);
    }
  };

  const isSubmitting = createLoading || updateLoading;

  const handleContinue = () => {
    if (createdEntityId) {
      onNext();
    }
  };

  // Loading state for existing entity
  if (existingEntityId && entityLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if ((createSuccess || (isEditMode && createdEntityId)) && createdEntityId) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {isEditMode ? 'Billing identity updated!' : 'Billing identity added!'}
          </Typography>
          <Typography variant="body2">
            Next, we'll verify your information with the clearinghouse.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={onBack} fullWidth>
            Back
          </Button>
          <Button variant="contained" onClick={handleContinue} fullWidth>
            Continue to Verify
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {isEditMode ? 'Edit Billing Identity' : 'Add Billing Identity'}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {isEditMode
          ? 'Update your billing information and try verifying again.'
          : 'Your billing identity is the NPI and Tax ID that appears on claims, identifying your practice to insurance companies.'}
      </Typography>

      {isEditMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Fix any issues below and save to retry verification. Current Tax ID is masked for security - leave blank to keep existing.
        </Alert>
      )}

      <Box component="form" sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          {/* NPI */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Billing NPI"
              value={formData.billing_npi}
              onChange={handleChange('billing_npi')}
              fullWidth
              required
              error={!!errors.billing_npi}
              helperText={errors.billing_npi || '10-digit National Provider Identifier'}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          {/* Tax ID Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Tax ID Type</InputLabel>
              <Select
                value={formData.tax_id_type}
                label="Tax ID Type"
                onChange={e =>
                  setFormData(prev => ({ ...prev, tax_id_type: e.target.value }))
                }
              >
                <MenuItem value="EIN">EIN (Employer Identification Number)</MenuItem>
                <MenuItem value="SSN">SSN (Social Security Number)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Tax ID */}
          <Grid item xs={12}>
            <TextField
              label={`Tax ID (${formData.tax_id_type})`}
              value={formData.tax_id}
              onChange={handleChange('tax_id')}
              fullWidth
              required={!isEditMode}
              error={!!errors.tax_id}
              helperText={
                errors.tax_id ||
                (isEditMode
                  ? `Current: ${existingEntity?.tax_id_masked || 'unknown'} - Leave blank to keep, or enter new value`
                  : 'Format: XX-XXXXXXX (EIN) or XXX-XX-XXXX (SSN)')
              }
              placeholder={isEditMode ? 'Leave blank to keep current' : ''}
            />
          </Grid>

          {/* Legal Name */}
          <Grid item xs={12}>
            <TextField
              label="Legal Name"
              value={formData.legal_name}
              onChange={handleChange('legal_name')}
              fullWidth
              required
              error={!!errors.legal_name}
              helperText={errors.legal_name || 'Full legal name of the entity'}
            />
          </Grid>

          {/* Doing Business As (optional) */}
          <Grid item xs={12}>
            <TextField
              label="Doing Business As (DBA)"
              value={formData.doing_business_as}
              onChange={handleChange('doing_business_as')}
              fullWidth
              helperText="Optional - if different from legal name"
            />
          </Grid>

          {/* Address Line 1 */}
          <Grid item xs={12}>
            <TextField
              label="Address Line 1"
              value={formData.billing_address.line1}
              onChange={handleAddressChange('line1')}
              fullWidth
              required
              error={!!errors.billing_address}
            />
          </Grid>

          {/* Address Line 2 */}
          <Grid item xs={12}>
            <TextField
              label="Address Line 2"
              value={formData.billing_address.line2}
              onChange={handleAddressChange('line2')}
              fullWidth
              helperText="Suite, unit, etc. (optional)"
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              value={formData.billing_address.city}
              onChange={handleAddressChange('city')}
              fullWidth
              required
              error={!!errors.billing_address}
            />
          </Grid>

          {/* State */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="State"
              value={formData.billing_address.state}
              onChange={handleAddressChange('state')}
              fullWidth
              required
              error={!!errors.billing_address}
              helperText="2-letter code (e.g., CA)"
              inputProps={{ maxLength: 2 }}
            />
          </Grid>

          {/* ZIP */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="ZIP Code"
              value={formData.billing_address.zip}
              onChange={handleAddressChange('zip')}
              fullWidth
              required
              error={!!errors.billing_address}
              helperText="XXXXX or XXXXX-XXXX"
            />
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Contact Information
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Required for clearinghouse communication about claims
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Contact Name"
              value={formData.contact_name}
              onChange={handleChange('contact_name')}
              fullWidth
              helperText="Optional"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Contact Email"
              type="email"
              value={formData.contact_email}
              onChange={handleChange('contact_email')}
              fullWidth
              required
              error={!!errors.contact_email}
              helperText={errors.contact_email || 'For claim status notifications'}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Contact Phone"
              value={formData.contact_phone}
              onChange={handleChange('contact_phone')}
              fullWidth
              required
              error={!!errors.contact_phone}
              helperText={errors.contact_phone || 'For urgent claim issues'}
            />
          </Grid>
        </Grid>

        {createError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {createError && typeof createError === 'object' && 'data' in createError && createError.data && typeof createError.data === 'object' && 'message' in createError.data
              ? String(createError.data.message)
              : 'Failed to create billing entity. Please check your information and try again.'}
          </Alert>
        )}

        {errors.billing_address && (
          <FormHelperText error sx={{ mt: 2 }}>
            {errors.billing_address}
          </FormHelperText>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onBack} disabled={isSubmitting} fullWidth>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting
              ? isEditMode
                ? 'Saving...'
                : 'Adding...'
              : isEditMode
                ? 'Save & Retry Verification'
                : 'Add Billing Identity'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
